import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, PlanetLoader, Input, Select } from '../components';
import Background from '../components/Background';
import { getBackendSessionCached, getPersonalityCached, getUserProfileCached, updateUserProfile, updateUserPreferences } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AsyncSelect from 'react-select/async';
import { CityData } from '../data/cities';
import { searchCities } from '../services/geonames';
import logger from '../utils/logger';

interface PersonalityData {
  user_id: string;
  personality_summary: string;
  personality_keywords: string[];
  generated_at: string;
}

const PersonalitySummary: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, signOut } = useAuth();
  const [personalityData, setPersonalityData] = useState<PersonalityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [birthChart, setBirthChart] = useState<any>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  
  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    gender: '',
    birth_date: null as Date | null,
    birth_hour: '',
    birth_minute: '',
    birth_ampm: '',
    birth_city: null as CityData | null,
    astrology_style: 'western'
  });

  const planets = [
    { name: 'Sun', symbol: '☉', color: '#FFD700' },
    { name: 'Moon', symbol: '☽', color: '#E6E6FA' },
    { name: 'Mercury', symbol: '☿', color: '#87CEEB' },
    { name: 'Venus', symbol: '♀', color: '#FFB6C1' },
    { name: 'Mars', symbol: '♂', color: '#FF6347' },
    { name: 'Jupiter', symbol: '♃', color: '#9370DB' },
    { name: 'Saturn', symbol: '♄', color: '#4B0082' },
    { name: 'Uranus', symbol: '⛢', color: '#00CED1' },
    { name: 'Neptune', symbol: '♆', color: '#4169E1' }
  ];

  useEffect(() => {
    logger.componentMount('PersonalitySummary', { user: user?.email || null, authLoading });
    return () => {
      logger.componentUnmount('PersonalitySummary');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, values are for logging only

  useEffect(() => {
    const fetchPersonalitySummary = async () => {
      // Wait for auth to load
      if (authLoading) {
        logger.debug('PersonalitySummary', 'Waiting for auth to load');
        return;
      }

      // Check if user is authenticated
      if (!user) {
        logger.warn('PersonalitySummary', 'No user found, redirecting to landing');
        navigate('/');
        return;
      }

      try {
        setLoading(true);
        logger.info('PersonalitySummary', 'Fetching personality summary', { userEmail: user.email });
        
        // First, get the backend user_id from the session endpoint
        logger.apiCall('PersonalitySummary', 'GET', '/auth/session (cached)');
        const sessionResponse = await getBackendSessionCached();
        logger.apiResponse('PersonalitySummary', 'GET', '/auth/session', 200, { user_id: sessionResponse.data.user_id });
        const backendUserId = sessionResponse.data.user_id;

        if (!backendUserId) {
          throw new Error('Unable to get user ID from session');
        }

        // Then fetch the personality summary using the backend user_id
        logger.apiCall('PersonalitySummary', 'GET', `/chart/personality/${backendUserId} (cached)`);
        const response = await getPersonalityCached(backendUserId);
        logger.apiResponse('PersonalitySummary', 'GET', `/chart/personality/${backendUserId}`, 200, { hasData: !!response.data });
        setPersonalityData(response.data);
      } catch (err: any) {
        logger.error('PersonalitySummary', 'Error fetching personality summary', err, { 
          status: err.response?.status,
          detail: err.response?.data?.detail 
        });
        setError(err.response?.data?.detail || 'Failed to load personality summary');
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalitySummary();
  }, [navigate, user, authLoading]);

  const handleContinue = () => {
    logger.userAction('PersonalitySummary', 'handleContinue');
    navigate('/home');
  };

  const handleLogout = async () => {
    logger.userAction('PersonalitySummary', 'handleLogout');
    try {
      setIsLoggingOut(true);
      await signOut();
      logger.info('PersonalitySummary', 'Logout successful, redirecting to landing');
      navigate('/', { replace: true });
    } catch (err: any) {
      logger.error('PersonalitySummary', 'Logout error', err);
      setError('Failed to sign out. Please try again.');
      setIsLoggingOut(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const profileResponse = await getUserProfileCached();
      setUserProfile(profileResponse.data);
      
      // Fetch birth chart if available
      if (profileResponse.data.has_birth_chart) {
        const backendUserId = (await getBackendSessionCached()).data.user_id;
        const chartResponse = await api.get(`/chart/${backendUserId}`);
        setBirthChart(chartResponse.data);
      }
    } catch (err: any) {
      logger.error('PersonalitySummary', 'Error fetching user data', err);
    }
  };

  const handleOpenEdit = async () => {
    logger.userAction('PersonalitySummary', 'handleOpenEdit');
    setShowEditModal(true);
    setEditError(null);
    
    // Fetch user data if not already loaded
    if (!userProfile) {
      await fetchUserData();
    }
    
    // Populate form with current data
    if (userProfile) {
      const profile = userProfile;
      const chart = birthChart;
      
      // Parse birth time if available
      let parsedHour = '';
      let parsedMinute = '';
      let parsedAmpm = '';
      
      if (chart?.birth_time) {
        const timeStr = chart.birth_time;
        const [hourStr, minuteStr] = timeStr.split(':');
        const hour24 = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);
        
        // Convert to 12-hour format
        if (hour24 === 0) {
          parsedHour = '12';
          parsedAmpm = 'AM';
        } else if (hour24 === 12) {
          parsedHour = '12';
          parsedAmpm = 'PM';
        } else if (hour24 > 12) {
          parsedHour = (hour24 - 12).toString();
          parsedAmpm = 'PM';
        } else {
          parsedHour = hour24.toString();
          parsedAmpm = 'AM';
        }
        
        parsedMinute = minute.toString().padStart(2, '0');
      }
      
      setEditFormData({
        name: profile.user?.name || '',
        gender: profile.user?.gender || '',
        birth_date: chart?.birth_date ? new Date(chart.birth_date) : null,
        birth_hour: parsedHour,
        birth_minute: parsedMinute,
        birth_ampm: parsedAmpm,
        birth_city: chart ? {
          name: chart.birth_city,
          country: chart.birth_country,
          lat: parseFloat(chart.birth_latitude),
          lng: parseFloat(chart.birth_longitude)
        } : null,
        astrology_style: profile.prefs?.astrology_style || 'western'
      });
    }
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditError(null);
  };

  const handleEditSubmit = async () => {
    logger.userAction('PersonalitySummary', 'handleEditSubmit');
    setEditLoading(true);
    setEditError(null);
    
    try {
      // Update user profile
      await updateUserProfile({
        name: editFormData.name.trim() || undefined,
        gender: editFormData.gender || undefined
      });
      
      // Update preferences
      await updateUserPreferences({
        astrology_style: editFormData.astrology_style
      });
      
      // If birth data changed, recalculate chart
      if (editFormData.birth_date && editFormData.birth_city) {
        let birth_time = null;
        if (editFormData.birth_hour && editFormData.birth_minute && editFormData.birth_ampm) {
          let hour = parseInt(editFormData.birth_hour);
          if (editFormData.birth_ampm === 'PM' && hour !== 12) {
            hour += 12;
          } else if (editFormData.birth_ampm === 'AM' && hour === 12) {
            hour = 0;
          }
          birth_time = `${hour.toString().padStart(2, '0')}:${editFormData.birth_minute}`;
        }
        
        const chartData = {
          birth_date: editFormData.birth_date.toISOString().split('T')[0],
          birth_time: birth_time,
          birth_city: editFormData.birth_city.name,
          birth_country: editFormData.birth_city.country,
          birth_latitude: editFormData.birth_city.lat,
          birth_longitude: editFormData.birth_city.lng
        };
        
        await api.post('/chart/calculate', chartData);
      }
      
      // Refresh data
      await fetchUserData();
      const backendUserId = (await getBackendSessionCached()).data.user_id;
      const response = await getPersonalityCached(backendUserId);
      setPersonalityData(response.data);
      
      setShowEditModal(false);
      logger.info('PersonalitySummary', 'Edit successful');
    } catch (err: any) {
      logger.error('PersonalitySummary', 'Edit error', err);
      setEditError(err.response?.data?.detail || 'Failed to update details. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#050508' }}>
        <Background />
        <PlanetLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-6" style={{ backgroundColor: '#050508' }}>
        <Background />
        <div className="max-w-md w-full relative z-10">
          <div 
            className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl text-center"
            style={{
              borderColor: 'rgba(239, 68, 68, 0.3)',
              backgroundColor: 'rgba(20, 20, 32, 0.3)',
              boxShadow: '0 0 40px rgba(239, 68, 68, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <h2 className="text-xl font-heading text-red-400 mb-4 tracking-luxury-tight font-semibold">Error</h2>
            <p className="text-text-secondary mb-6">{error}</p>
            <Button onClick={() => navigate('/onboarding')} variant="secondary">
              Back to Onboarding
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!personalityData) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-6" style={{ backgroundColor: '#050508' }}>
        <Background />
        <div className="max-w-md w-full relative z-10">
          <div 
            className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl text-center"
            style={{
              borderColor: 'rgba(255, 184, 79, 0.2)',
              backgroundColor: 'rgba(20, 20, 32, 0.3)',
              boxShadow: '0 0 40px rgba(255, 184, 79, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <h2 className="text-xl font-heading text-white mb-4 tracking-luxury-tight font-semibold">No Personality Data</h2>
            <p className="text-text-secondary mb-6">
              Your birth chart hasn't been calculated yet. Let's complete your chart first.
            </p>
            <Button onClick={() => navigate('/onboarding')} variant="primary">
              Complete Onboarding
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#050508' }}>
      <Background />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 animate-fade-in">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gradient-gold mb-3 sm:mb-4 tracking-luxury">
            Your Cosmic Blueprint
          </h1>
          <p className="text-text-secondary font-elegant tracking-luxury-tight italic text-base sm:text-lg md:text-xl">
            A mystical revelation of your soul's essence
          </p>
        </div>

        {/* Planet Row - using outline circles with symbols like landing page */}
        <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 mb-8 sm:mb-12 animate-fade-in">
          {planets.map((planet, index) => (
            <div
              key={planet.name}
              className="flex flex-col items-center group cursor-default"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-base sm:text-lg mb-2
                           transition-all duration-300 group-hover:scale-110 font-medium"
                style={{
                  border: `2.5px solid ${planet.color}`,
                  backgroundColor: 'transparent',
                  boxShadow: `0 0 12px ${planet.color}70, 0 0 25px ${planet.color}40`,
                  animation: 'float 3s ease-in-out infinite'
                }}
              >
                <span 
                  style={{ 
                    color: planet.color,
                    textShadow: `0 0 8px ${planet.color}60`
                  }}
                >
                  {planet.symbol}
                </span>
              </div>
              <span className="text-xs sm:text-sm text-text-secondary font-heading tracking-luxury-tight">{planet.name}</span>
            </div>
          ))}
        </div>

        {/* Personality Summary */}
        <div className="max-w-4xl mx-auto animate-fade-in">
          <div className="glass-grain rounded-2xl px-6 py-5 sm:px-8 sm:py-6 mb-8 backdrop-blur-xl border shadow-2xl" style={{
            borderColor: 'rgba(255, 184, 79, 0.2)',
            backgroundColor: 'rgba(20, 20, 32, 0.3)',
            boxShadow: '0 0 40px rgba(255, 184, 79, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
          }}>
            <div className="prose prose-lg prose-invert max-w-none">
              <div
                className="text-text-secondary leading-relaxed font-elegant text-base sm:text-lg tracking-luxury-tight"
                style={{ lineHeight: '1.9' }}
              >
                {personalityData.personality_summary.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-6">
                    {paragraph.split('\n').map((line, lineIndex) => (
                      <React.Fragment key={lineIndex}>
                        {line}
                        {lineIndex < paragraph.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                ))}
              </div>
            </div>

            {/* Keywords */}
            {personalityData.personality_keywords && personalityData.personality_keywords.length > 0 && (
              <div className="mt-8 sm:mt-10">
                <h3 className="text-lg sm:text-xl font-heading text-gradient-gold mb-4 sm:mb-6 text-center tracking-luxury-tight font-semibold">
                  Key Cosmic Traits
                </h3>
                <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                  {personalityData.personality_keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 glass-grain border border-cosmic-gold/30
                                 rounded-full text-sm sm:text-base text-text-secondary font-elegant tracking-luxury-tight backdrop-blur-xl
                                 transition-all duration-300 hover:border-cosmic-gold/50 hover:scale-105"
                      style={{
                        backgroundColor: 'rgba(20, 20, 32, 0.3)'
                      }}
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Continue Button */}
          <div className="text-center mb-8">
            <Button
              onClick={handleContinue}
              className="min-w-[200px] sm:min-w-[240px] text-base sm:text-lg px-8 py-3 sm:py-4"
              variant="secondary"
            >
              <span className="flex items-center justify-center gap-2">
                Begin Your Cosmic Journey
                <svg 
                  className="w-4 h-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Button>
          </div>
          
          {/* Edit Details Button */}
          <div className="text-center">
            <Button
              onClick={handleOpenEdit}
              variant="ghost"
              className="text-text-secondary hover:text-text-primary transition-all duration-300"
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Details
              </span>
            </Button>
          </div>
        </div>

        {/* Sign Out Button - Absolute Bottom */}
        <div className="text-center mt-4 pb-8">
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="glass-grain border border-cosmic-gold/30 hover:border-cosmic-gold/50 bg-space-dark/30 hover:bg-space-dark/50 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] px-5 py-2.5 rounded-xl text-text-secondary hover:text-text-primary font-elegant text-sm tracking-luxury-tight flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoggingOut ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing out...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </>
            )}
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={handleCloseEdit}
        >
          <div 
            className="glass-grain rounded-2xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto backdrop-blur-xl border shadow-2xl animate-fade-in"
            style={{
              borderColor: 'rgba(255, 184, 79, 0.3)',
              backgroundColor: 'rgba(20, 20, 32, 0.95)',
              boxShadow: '0 16px 64px rgba(0,0,0,0.6)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gradient-gold tracking-luxury">
                Edit Details
              </h2>
              <button
                onClick={handleCloseEdit}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editError && (
              <div className="mb-6 p-4 glass-grain rounded-xl border border-red-500/30 backdrop-blur-xl text-red-400 animate-fade-in" style={{
                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                boxShadow: '0 0 20px rgba(255, 107, 107, 0.2)'
              }}>
                <p className="font-elegant text-sm">{editError}</p>
              </div>
            )}

            <div className="space-y-5">
              <Input
                label="Name"
                name="name"
                value={editFormData.name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your name"
              />

              <Select
                label="Gender"
                name="gender"
                value={editFormData.gender}
                onChange={(e) => setEditFormData(prev => ({ ...prev, gender: e.target.value }))}
                options={[
                  { value: '', label: 'Prefer not to say' },
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'other', label: 'Other' }
                ]}
              />

              <div className="w-full group">
                <label className="block text-base font-medium text-text-secondary mb-2.5 font-elegant">
                  Birth Date *
                </label>
                <DatePicker
                  selected={editFormData.birth_date}
                  onChange={(date) => setEditFormData(prev => ({ ...prev, birth_date: date }))}
                  dateFormat="MMMM d, yyyy"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  maxDate={new Date()}
                  className="w-full px-4 py-3 rounded-xl bg-space-dark bg-opacity-60 border border-border-subtle text-text-primary placeholder-text-tertiary font-body text-base sm:text-lg focus:outline-none focus:border-cosmic-gold focus:ring-2 focus:ring-cosmic-gold focus:ring-opacity-25 transition-all duration-300 group-hover:border-cosmic-gold/50"
                  placeholderText="Select your birth date"
                />
              </div>

              <div className="w-full">
                <label className="block text-base font-medium text-text-secondary mb-2.5 font-elegant">
                  Birth Time (Optional)
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <Select
                    label=""
                    name="birth_hour"
                    value={editFormData.birth_hour}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, birth_hour: e.target.value }))}
                    options={[
                      { value: '', label: 'Hour' },
                      ...Array.from({ length: 12 }, (_, i) => ({
                        value: (i + 1).toString(),
                        label: (i + 1).toString()
                      }))
                    ]}
                  />
                  <Select
                    label=""
                    name="birth_minute"
                    value={editFormData.birth_minute}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, birth_minute: e.target.value }))}
                    options={[
                      { value: '', label: 'Min' },
                      ...Array.from({ length: 60 }, (_, i) => ({
                        value: i.toString().padStart(2, '0'),
                        label: i.toString().padStart(2, '0')
                      }))
                    ]}
                  />
                  <Select
                    label=""
                    name="birth_ampm"
                    value={editFormData.birth_ampm}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, birth_ampm: e.target.value }))}
                    options={[
                      { value: '', label: 'AM/PM' },
                      { value: 'AM', label: 'AM' },
                      { value: 'PM', label: 'PM' }
                    ]}
                  />
                </div>
              </div>

              <div className="w-full group">
                <label className="block text-base font-medium text-text-secondary mb-2.5 font-elegant">
                  Birth City *
                </label>
                <AsyncSelect
                  value={editFormData.birth_city ? {
                    value: editFormData.birth_city,
                    label: `${editFormData.birth_city.name}, ${editFormData.birth_city.country}`
                  } : null}
                  onChange={(selected) => setEditFormData(prev => ({ ...prev, birth_city: selected?.value || null }))}
                  loadOptions={async (inputValue: string) => {
                    if (!inputValue || inputValue.length < 2) return [];
                    const cities = await searchCities(inputValue, 20);
                    return cities.map(city => ({
                      value: city,
                      label: `${city.name}, ${city.country}${city.region ? ` (${city.region})` : ''}`
                    }));
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Search and select your birth city..."
                  isSearchable={true}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      backgroundColor: 'rgba(20, 20, 32, 0.6)',
                      borderColor: state.isFocused ? '#FFB84F' : 'rgba(255, 184, 79, 0.2)',
                      borderRadius: '0.75rem',
                      padding: '0.25rem',
                      color: '#F5F5F5',
                      transition: 'all 0.3s ease',
                      '&:hover': { borderColor: 'rgba(255, 184, 79, 0.5)' },
                      fontSize: '1rem',
                      fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                      boxShadow: state.isFocused ? '0 0 0 3px rgba(255, 184, 79, 0.15)' : 'none',
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: '#F5F5F5',
                      fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#6B6B75',
                      fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: 'rgba(20, 20, 32, 0.95)',
                      backdropFilter: 'blur(16px)',
                      borderRadius: '0.75rem',
                      border: '1px solid rgba(255, 184, 79, 0.3)',
                      boxShadow: '0 16px 64px rgba(0, 0, 0, 0.6), 0 0 40px rgba(255, 184, 79, 0.1)',
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 10000,
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected 
                        ? 'rgba(255, 184, 79, 0.3)' 
                        : state.isFocused 
                        ? 'rgba(255, 184, 79, 0.1)' 
                        : 'transparent',
                      color: state.isSelected ? '#FFB84F' : '#F5F5F5',
                      fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: 'rgba(255, 184, 79, 0.15)' },
                      fontSize: '0.875rem',
                      padding: '10px 12px',
                    }),
                    input: (provided) => ({
                      ...provided,
                      color: '#F5F5F5',
                      fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                    }),
                  }}
                />
              </div>

              <div className="space-y-4">
                <label className="block text-base font-medium text-text-secondary mb-2.5 font-elegant">
                  Astrology Style
                </label>
                <div className="space-y-3">
                  <label 
                    className="flex items-start gap-4 p-4 glass-grain rounded-xl cursor-pointer transition-all duration-300 hover:scale-[1.01] group relative"
                    style={{
                      border: editFormData.astrology_style === 'western' 
                        ? '2px solid rgba(255, 184, 79, 0.5)' 
                        : '2px solid rgba(255, 184, 79, 0.2)',
                      backgroundColor: editFormData.astrology_style === 'western' 
                        ? 'rgba(255, 184, 79, 0.08)' 
                        : 'rgba(20, 20, 32, 0.3)',
                      boxShadow: editFormData.astrology_style === 'western'
                        ? '0 0 20px rgba(255, 184, 79, 0.15)'
                        : '0 4px 16px rgba(0,0,0,0.2)'
                    }}
                  >
                    <input
                      type="radio"
                      name="astrology_style"
                      value="western"
                      checked={editFormData.astrology_style === 'western'}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, astrology_style: e.target.value }))}
                      className="mt-1 w-4 h-4 accent-cosmic-gold cursor-pointer"
                    />
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-text-primary text-base mb-1">Western Astrology</h3>
                      <p className="text-sm text-text-secondary font-elegant leading-relaxed">
                        Based on tropical zodiac. Most common in Western cultures.
                      </p>
                    </div>
                  </label>
                  
                  <label 
                    className="flex items-start gap-4 p-4 glass-grain rounded-xl cursor-pointer transition-all duration-300 hover:scale-[1.01] group relative"
                    style={{
                      border: editFormData.astrology_style === 'vedic' 
                        ? '2px solid rgba(255, 184, 79, 0.5)' 
                        : '2px solid rgba(255, 184, 79, 0.2)',
                      backgroundColor: editFormData.astrology_style === 'vedic' 
                        ? 'rgba(255, 184, 79, 0.08)' 
                        : 'rgba(20, 20, 32, 0.3)',
                      boxShadow: editFormData.astrology_style === 'vedic'
                        ? '0 0 20px rgba(255, 184, 79, 0.15)'
                        : '0 4px 16px rgba(0,0,0,0.2)'
                    }}
                  >
                    <input
                      type="radio"
                      name="astrology_style"
                      value="vedic"
                      checked={editFormData.astrology_style === 'vedic'}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, astrology_style: e.target.value }))}
                      className="mt-1 w-4 h-4 accent-cosmic-gold cursor-pointer"
                    />
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-text-primary text-base mb-1">Vedic Astrology</h3>
                      <p className="text-sm text-text-secondary font-elegant leading-relaxed">
                        Based on sidereal zodiac. Traditional Indian astrology system.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <Button
                onClick={handleCloseEdit}
                variant="ghost"
                className="text-text-secondary hover:text-text-primary transition-all duration-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditSubmit}
                isLoading={editLoading}
                className="glass-grain border border-cosmic-gold/40 hover:border-cosmic-gold/60 bg-space-dark/40 hover:bg-space-dark/60 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02]"
                variant="secondary"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalitySummary;
